import React, { Fragment, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Grid, Paper, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import classNames from 'classnames';

import lang from '../../../lib/language';
import { ReportingCall } from '../../../lib/models/reporting/types';
import { RootState } from '../../../lib/store';
import { CRMCall } from '../../../lib/store/crm/types';
import { PositionType } from '../../../lib/store/reporting/types';
import { hammerBasicColors } from '../../../lib/util/chart-colors';
import { isCallStatusPending } from '../../../lib/util/error-handling/StatusByCallHelpers';

import { useIsMobile } from '../../../hooks';
import { UI_ROUTES } from '../../../util/routes';
import CompanyLogo from '../../CompanyLogo/CompanyLogo';
import MyAccountButtons from '../../SwipeableItems/MyAccountButtons';
import { WATCH_LIST_TABLE_ITEMS } from '../../WatchList/WatchList.constans';

import SkeletonLoaderRow from './SkeletonLoaderRow/SkeletonLoaderRow';
import TableRows from './TableRows';

import styles from '../TableView.module.scss';

type OwnProps = {
  tableHeaders: { [symbol: string]: string | any };
  data: any;
  isMultipleDivOfRow: boolean;
  initiallyLengthRows?: number;
  isButtonsInRow?: boolean;
  isModifyButtons?: boolean;
  companyLogos?: boolean;
  otherButtons?: string;
  hasSymbol?: boolean;
  pendingOrders?: boolean;
};

type Props = OwnProps & ReduxProps;

let symbol: string = '';

const TableContainerView = (props: Props) => {
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const [selectedSymbol, setSelectedSymbol] = useState<string | undefined>();
  const [isSellOrBuySelected, setIsSellOrBuySelected] = useState<boolean>(false);

  const {
    tableHeaders,
    data,
    isMultipleDivOfRow,
    isButtonsInRow,
    isModifyButtons,
    companyLogos,
    otherButtons,
    companyLogoAndNameData,
    hasSymbol,
    initiallyLengthRows = Number.MAX_VALUE,
    pendingOrders,
    isTradingPlanPending,
    isPendingOrdersLoading,
    isOpenPositionPending,
  } = props;

  const hasData = data?.length > 0;
  const isLoading = !hasData
    && (isTradingPlanPending
      || isPendingOrdersLoading
      || isOpenPositionPending);

  useEffect(() => {
    const handleClickInside = (event) => {
      if (
        symbol
        && event
          .composedPath()
          .some(
            (el) => el.className === 'MuiTableRow-root TableView_clickableParrentTableRow__y9ohl',
          )
        && !event.target.className.includes('ButtonsSellAndBuy')
        && !event.target.className.includes('ButtonsCancelAndModify')
        && !event.target.className.includes('wrapperButtons')
      ) {
        navigate(`${UI_ROUTES.symbol}/${symbol}`);
      }
    };

    document.addEventListener('click', handleClickInside);
    return () => document.removeEventListener('click', handleClickInside);
  }, [navigate, isMobile]);

  const handleClickRow = (
    event: React.MouseEvent<HTMLTableRowElement>,
    symbolName: string,
    isSelectedRow?: boolean,
  ) => {
    symbol = symbolName;
    const target = event.target as HTMLElement;
    if (
      isSelectedRow
      && !isSellOrBuySelected
      && !target.className.includes('ButtonsSellAndBuy')
      && !target.className.includes('ButtonsCancelAndModify')
      && !target.className.includes('modifyButton')
      && !target.className.includes('input')
      && !target.className.includes('jss')
    ) {
      navigate(`${UI_ROUTES.symbol}/${symbol}`);
    }
  };

  return (
    <div className={isModifyButtons ? styles.tableWrapperFullWidth : styles.tableWrapper}>
      {isMobile ? (
        <MyAccountButtons
          data={data}
          hasSymbol={hasSymbol}
          companyLogos={companyLogos}
          pendingOrders={pendingOrders}
          companyLogoAndNameData={companyLogoAndNameData}
        />
      ) : (
        <Paper
          style={{
            backgroundColor: 'transparent',
            boxShadow: 'none',
          }}
        >
          <TableContainer className={styles.wrapperTable}>
            <Table stickyHeader>
              <TableBody>
                <TableRow>
                  {tableHeaders.map((column) => (
                    <TableCell key={column.key} className={styles.tableHeader}>
                      <Grid
                        className={classNames({
                          [styles.buttons]: isButtonsInRow,
                          [styles.tableHeaderText]: !isButtonsInRow,
                          [styles.modifyButtons]: isModifyButtons,
                        })}
                      >
                        {column.props.children}
                      </Grid>
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
              <TableBody>
                {isLoading ? (
                  <>
                    <SkeletonLoaderRow isPendingOrder={pendingOrders} key={1} />
                    <SkeletonLoaderRow isPendingOrder={pendingOrders} key={2} />
                    <SkeletonLoaderRow isPendingOrder={pendingOrders} key={3} />
                    <SkeletonLoaderRow isPendingOrder={pendingOrders} key={4} />
                  </>
                ) : (
                  data.map((row, index: number) => {
                    let companyLogoAndName;
                    if (!hasSymbol) {
                      companyLogoAndName = companyLogoAndNameData[row.name];
                    }

                    return (
                      <Fragment key={row.id}>
                        {index < initiallyLengthRows && (
                          <TableRow
                            key={row.id}
                            className={classNames({
                              [styles.clickableParrentTableRow]: row.name,
                              [styles.noClickableParrentTableRow]: !row.name,
                              [styles.hoveredItem]: row.name === selectedSymbol,
                            })}
                            onClick={(event) =>
                              handleClickRow(event, row.name, row.name === selectedSymbol)}
                            onMouseEnter={() => setSelectedSymbol && setSelectedSymbol(row.name)}
                            onMouseLeave={() => setSelectedSymbol && setSelectedSymbol('')}
                          >
                            {isMultipleDivOfRow && !hasSymbol && (
                              <td className={styles.tableRow}>
                                <Grid className={styles.wrapperFirstColumn}>
                                  <div
                                    style={{ background: hammerBasicColors[index] }}
                                    className={styles.colorVerticalLine}
                                  />
                                  {companyLogos ? (
                                    <CompanyLogo
                                      url={companyLogoAndName?.url}
                                      logo={companyLogoAndName?.logo}
                                      className={styles.image}
                                      symbol={row.name}
                                    />
                                  ) : (
                                    <img src={row.icon} className={styles.image} alt="" />
                                  )}
                                  <Grid className={styles.columnName}>
                                    <Grid>
                                      <span className={styles.symbol}>{row.name}</span>
                                      {row.positionType === PositionType.Sell && (
                                        <span
                                          className={styles.positionType}
                                        >
                                          {` ${lang.commonShort()}`}
                                        </span>
                                      )}
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </td>
                            )}
                            {Object.keys(row).map((key) => {
                              const isCheckItem = WATCH_LIST_TABLE_ITEMS.includes(key);

                              if (isCheckItem) {
                                return (
                                  <TableRows
                                    key={key}
                                    row={{ ...row, companyName: companyLogoAndName?.name }}
                                    rowKey={row[key]}
                                    headerKey={key}
                                    otherButtons={otherButtons}
                                    setIsSellOrBuySelected={setIsSellOrBuySelected}
                                    showButtons={row.name === selectedSymbol}
                                  />
                                );
                              }
                              return null;
                            })}
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  companyLogoAndNameData: state.fundamental.companyLogoAndName,
  isPendingOrdersLoading: isCallStatusPending(
    ReportingCall.getAccountPendingOrders,
    state.reporting.statusByCall,
  ),
  isOpenPositionPending: isCallStatusPending(
    ReportingCall.getOpenPositions,
    state.reporting.statusByCall,
  ),
  isTradingPlanPending: isCallStatusPending(CRMCall.searchTradingPlans, state.crm.statusByCall),
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(TableContainerView);
